import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Rocket, Warning, ArrowRight, CalendarBlank, Check, ArrowSquareOut } from "@phosphor-icons/react";
import { Calendar } from "@/components/ui/calendar";
import PaymentTrust from "@/components/PaymentTrust";
import { buildCheckoutUrl } from "@/lib/checkoutUrl";

function toISO(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function StartPages() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [websites, setWebsites] = useState([]);
  const [selectedSite, setSelectedSite] = useState("");
  const [reservedDates, setReservedDates] = useState([]);
  const [myReservation, setMyReservation] = useState(null);
  const [date, setDate] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [c, ws, rd, mine] = await Promise.all([
        api.get("/start-pages/config"),
        api.get("/websites"),
        api.get("/start-pages/reserved-dates"),
        api.get("/start-pages/my-reservation"),
      ]);
      setCfg(c.data);
      const approved = (ws.data || []).filter((w) => w.status === "approved");
      setWebsites(approved);
      if (approved.length && !selectedSite) setSelectedSite(approved[0].id);
      setReservedDates((rd.data?.dates || []).map((s) => new Date(s + "T00:00:00")));
      setMyReservation(mine.data?.reservation || null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, []);

  const disabledMatcher = useMemo(() => {
    const set = new Set(reservedDates.map((d) => toISO(d)));
    return (d) => {
      // Disable past days
      const today = new Date(); today.setHours(0, 0, 0, 0);
      if (d < today) return true;
      return set.has(toISO(d));
    };
  }, [reservedDates]);

  const reserve = async () => {
    if (!selectedSite) return toast.error("Pick one of your approved websites first");
    if (!date) return toast.error("Pick a day on the calendar first");
    setBusy(true);
    try {
      const { data } = await api.post("/start-pages/reserve", {
        website_id: selectedSite,
        date: toISO(date),
      });
      if (data.checkout_url) {
        const url = buildCheckoutUrl(data.checkout_url, user, {
          date: data.date,
          website_id: selectedSite,
        });
        window.open(url, "_blank", "noopener,noreferrer");
        toast.success(`Reservation held for ${data.date}. Finish payment in the new tab.`);
      } else {
        toast.success(`Reserved ${data.date}!`);
      }
      setDate(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <DashboardLayout><div className="text-sm text-slate-500">Loading…</div></DashboardLayout>;

  if (!cfg?.enabled) {
    return (
      <DashboardLayout>
        <div className="rounded-2xl border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/40 p-6 flex items-start gap-3 max-w-2xl">
          <Warning size={24} weight="fill" className="text-amber-500 mt-0.5" />
          <div>
            <h1 className="font-black text-slate-900 dark:text-white text-lg mb-1" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
              Start Pages are currently disabled
            </h1>
            <p className="text-sm text-slate-700 dark:text-slate-200">The admin hasn&apos;t enabled the Start Pages feature yet. Check back later.</p>
            <Link to="/dashboard" className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-blue-700 dark:text-blue-400 hover:underline">
              Back to dashboard <ArrowRight size={12} weight="bold" />
            </Link>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="mb-5">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">Advertise</div>
        <h1 className="text-3xl font-black tracking-tighter text-slate-900 dark:text-white inline-flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
          <Rocket size={28} weight="fill" className="text-blue-600" /> Start Pages
        </h1>
        <p className="text-sm text-slate-600 dark:text-slate-300 mt-1 max-w-2xl">
          Reserve a specific day of the month and every surfer who opens a session on that day will land on your site first.
        </p>
      </div>

      <div className="grid lg:grid-cols-5 gap-5">
        {myReservation && (
          <div className="lg:col-span-5 rounded-2xl border border-blue-300 dark:border-blue-500/40 bg-blue-50 dark:bg-blue-500/10 p-4 flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-slate-800 dark:text-slate-100">
              <span className="inline-block text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-blue-600 text-white mr-2">
                {myReservation.status}
              </span>
              You already booked <strong>{myReservation.date}</strong> for <strong>{myReservation.website_title}</strong>. Only one reservation at a time — wait for it to end, or cancel it to book a different day.
            </div>
            <button
              data-testid="start-pages-cancel-btn"
              onClick={async () => {
                if (!window.confirm(`Cancel your reservation for ${myReservation.date}?`)) return;
                try {
                  await api.delete(`/start-pages/my-reservation/${myReservation.id}`);
                  toast.success("Reservation cancelled");
                  load();
                } catch (e) { toast.error(formatApiError(e)); }
              }}
              className="rounded-full bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-xs font-bold px-3 py-1.5 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700"
            >
              Cancel reservation
            </button>
          </div>
        )}
        <div className="lg:col-span-3 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
          <div className="flex items-center gap-2 mb-3">
            <CalendarBlank size={18} weight="fill" className="text-blue-600" />
            <h2 className="font-black text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>Pick a day &amp; month</h2>
          </div>

          {websites.length === 0 ? (
            <div className="rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4 text-sm text-slate-600 dark:text-slate-300">
              You need at least one <b>approved</b> website first. <Link to="/websites" className="text-blue-700 dark:text-blue-400 hover:underline font-bold">Submit a website →</Link>
            </div>
          ) : (
            <>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Website</label>
              <select
                data-testid="start-pages-site-select"
                value={selectedSite}
                onChange={(e) => setSelectedSite(e.target.value)}
                className="w-full mt-1 mb-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100"
              >
                {websites.map((w) => (
                  <option key={w.id} value={w.id}>{w.title} — {w.url}</option>
                ))}
              </select>

              <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-800/50 inline-block">
                <Calendar
                  data-testid="start-pages-calendar"
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={disabledMatcher}
                  className="rdp"
                />
              </div>
              <div className="text-[11px] text-slate-500 mt-2">
                Grey days are already reserved by other advertisers.
              </div>
            </>
          )}
        </div>

        <div className="lg:col-span-2 rounded-3xl bg-gradient-to-br from-blue-50 to-white dark:from-slate-900 dark:to-slate-900 ring-2 ring-blue-400/50 p-6 flex flex-col">
          <div className="text-xs font-black uppercase tracking-widest text-blue-700 dark:text-blue-300">Reservation</div>
          <div className="text-4xl font-black text-slate-900 dark:text-white mt-1" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
            ${Number(cfg.daily_price_usd || 0).toFixed(2)}
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mb-4">per reserved day</div>

          <div className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4">
            <div className="flex items-center gap-2 text-sm">
              <CalendarBlank size={16} weight="fill" className="text-blue-600" />
              <span className="font-bold text-slate-900 dark:text-white">
                {date ? date.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" }) : "No date selected"}
              </span>
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 truncate">
              {websites.find((w) => w.id === selectedSite)?.url || "—"}
            </div>
          </div>

          <button
            data-testid="start-pages-reserve-btn"
            onClick={reserve}
            disabled={busy || !date || !selectedSite || !!myReservation}
            className="mt-5 inline-flex items-center justify-center gap-1 rounded-full bg-blue-700 hover:bg-blue-800 disabled:opacity-60 text-white font-black uppercase tracking-widest text-sm px-5 py-3 transition-colors"
          >
            {cfg.checkout_url ? (
              <>Reserve &amp; Checkout <ArrowSquareOut size={14} weight="bold" /></>
            ) : (
              <>Reserve day <Check size={14} weight="bold" /></>
            )}
          </button>

          <PaymentTrust />

          {!cfg.checkout_url && (
            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-2">
              Paid from your cash wallet.
            </p>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
